import React, { useEffect, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useParams } from "react-router-dom";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import useSWRMutation from "swr/mutation";
import { Group } from "src/interfaces/Group";
import ButtonBackPage from "@components/ButtonBackPage";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import {
  checkAssociationChanged,
  GetClusterData,
  LinkClusterBody,
} from "../types";

const AssociationLearningObject: React.FC = () => {
  const { id, initiativeId, clusterId } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["corporate.initiative.community-cluster.association.learning-object"]}`;

  const { t } = useTranslation(["community-cluster-page"]);

  const [loAssociation, setLoAssociation] = useState<Record<number, boolean>>(
    {},
  );

  const { columns, dataTable } = useFetchTable<Group[]>(
    `${ServicesURL.learningObjectsStandAlone.replace(":corporateId", `${id ? id : 1}`)}`,
    PagesID[
      "corporate.initiative.community-cluster.association.learning-object"
    ],
    filters[filterId],
  );

  const { dataDetail, mutate } = useFetchDataDetail(
    ServicesURL.getCommunityCluster
      .replace(":corporateId", id!)
      .replace(":initiativeId", initiativeId!)
      .replace(":clusterId", clusterId!),
    PagesID[
      "corporate.initiative.community-cluster.association.learning-object"
    ],
  );

  useEffect(() => {
    if (dataDetail) {
      setLoAssociation(
        ((dataDetail as GetClusterData).learning_objects ?? []).reduce<
          Record<number, boolean>
        >((agg, cur) => {
          agg[cur.lo_id] = true;
          return agg;
        }, {}),
      );
    }
  }, [dataDetail]);

  const { trigger: triggerLearningObjectAssociation } = useSWRMutation(
    ServicesURL.linkCommunityCluster
      .replace(":corporateId", id!)
      .replace(":initiativeId", initiativeId!)
      .replace(":clusterId", clusterId!),
    (url: string, { arg }: { arg: LinkClusterBody }) => {
      return fetcher<any>(`${url}`, MethodType.PUT, arg);
    },
  );

  const handleRowSelection = async (
    learningObjects: Record<number, boolean>,
  ) => {
    if (checkAssociationChanged(loAssociation, learningObjects)) {
      const learningObjectIds = Object.entries(learningObjects)
        .filter(([_, v]) => !!v)
        .map(([k]) => Number(k));
      const body: LinkClusterBody = {
        learning_objects: learningObjectIds,
      };
      await triggerLearningObjectAssociation(body);
      mutate && mutate();
      setLoAssociation(learningObjects);
    }
  };

  const name = !Array.isArray(dataDetail as GetClusterData | [])
    ? (dataDetail as GetClusterData | undefined)?.cluster.name
    : "";

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-corp-initiatives.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${name} - ${t("association-learning-objects.hero-title")}`}
          description={t("association-learning-objects.hero-description")}
        />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={
              PagesID[
                "corporate.initiative.community-cluster.association.learning-object"
              ]
            }
            data={dataTable?.output}
            columns={columns || []}
            configurationTopToolbar={[]}
            enableRowSelection={true}
            enableRowActions={false}
            enableMultiRowSelection={true}
            selectedRows={loAssociation}
            onRowSelectionChange={(groups: any) => {
              handleRowSelection(groups);
            }}
            getRowId={(originalRow: any) => originalRow?.id}
            totalPages={dataTable?.metadata?.total_elements}
            modAssociationEnabled={true}
          />
        </Box>
      </Container>
    </AnimationFadeIn>
  );
};

export default AssociationLearningObject;
